import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import SpinWheel from "../components/SpinWheel";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, Gift, ArrowLeft, ShoppingBag, Maximize2, X } from "lucide-react";

export default function LykkehjulPage() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [spinSequence, setSpinSequence] = useState(null);  // array av indekser for kjede-animasjon
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [animDone, setAnimDone] = useState(false);
  const [respinFlash, setRespinFlash] = useState(null);  // {label} – vises kort som overlay når et respin-segment treffes
  const [fullscreen, setFullscreen] = useState(false);
  // Anonyme spinn-felter (kun synlige når bruker ikke er innlogget)
  const [extPlatform, setExtPlatform] = useState("Snapchat");
  const [extHandle, setExtHandle] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/wheels/by-slug/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const spin = async () => {
    const c = code.trim().toUpperCase();
    if (!c) {
      toast.error(t("wheel.code_required"));
      return;
    }
    if (!user) {
      const p = extPlatform.trim();
      const h = extHandle.trim();
      if (!p || !h) {
        toast.error(t("wheel.contact_required"));
        return;
      }
    }
    setBusy(true);
    setAnimDone(false);
    setRespinFlash(null);
    try {
      const payload = { code: c, wheel_slug: slug };
      if (!user) {
        payload.external_platform = extPlatform.trim();
        payload.external_handle = extHandle.trim();
      }
      const r = await api.post("/wheels/redeem", payload);
      const spinData = r.data.spin;
      setResult(r.data);
      // Bruk hele kjeden hvis backend returnerte en – ellers fallback til enkel-spinn
      const sequence = Array.isArray(spinData.chain_indices) && spinData.chain_indices.length > 0
        ? spinData.chain_indices
        : [spinData.segment_index];
      setSpinSequence(sequence);
      // Sikkerhetsnett: ~6s per ledd + buffer (worst case 3 respin-treff = 4 ledd ≈ 18s)
      const safetyMs = sequence.length * 6000 + 2000;
      setTimeout(() => setAnimDone(true), safetyMs);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("wheel.redeem_failed"));
    } finally {
      setBusy(false);
    }
  };

  // Kalles av SpinWheel når hjulet stopper på et ledd i kjeden
  const handleSegmentReached = (segIdx, isLast) => {
    if (isLast) {
      setRespinFlash(null);
      return;
    }
    // Mellom-ledd = et respin-segment. Vis en kort flash før neste spinn starter.
    const seg = data?.wheel?.segments?.[segIdx];
    setRespinFlash({ label: seg?.label || "Spin igjen" });
    setTimeout(() => setRespinFlash(null), 1100);
  };

  if (loading) {
    return <Layout><div className="font-meta text-[var(--cream-soft)] py-20 text-center">{t("wheel.loading")}</div></Layout>;
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-20 text-center">
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-4">{t("wheel.not_found_title")}</h1>
          <p className="text-[var(--cream-soft)] mb-6">{t("wheel.not_found_text")}</p>
          <Link to="/" className="font-meta text-sm text-[var(--magenta)] underline-offset-4 hover:underline">
            {t("kontakt.back_to_home", "← Til forsiden")}
          </Link>
        </div>
      </Layout>
    );
  }

  const { wheel, creator } = data;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-6">
        <Link to={`/skaper/${creator.username}`} className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] hover:text-[var(--cream)]">
          <ArrowLeft className="h-3 w-3 inline mr-1" /> {creator.username?.toUpperCase()}S SKAPERSIDE
        </Link>

        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mt-3 mb-2 flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-[var(--magenta)]" />
          {wheel.title}
        </h1>
        {wheel.description && (
          <p className="text-[var(--cream-soft)] mb-6 font-manrope">{wheel.description}</p>
        )}

        {wheel.purchase_url && (
          <a
            href={wheel.purchase_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="purchase-link"
            className="inline-flex items-center gap-2 px-5 py-2.5 mb-6 border border-[var(--magenta)] text-[var(--magenta)] font-meta text-xs tracking-[0.2em] hover:bg-[var(--magenta)] hover:text-black transition"
          >
            <ShoppingBag className="h-4 w-4" /> KJØP SPINN-KODE →
          </a>
        )}

        <div className="border border-white/10 bg-[var(--noir-elev)] p-6 md:p-10 flex flex-col items-center relative" data-testid="spin-area">
          <button
            onClick={() => setFullscreen(true)}
            className="absolute top-3 right-3 p-2 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition"
            title="Fullskjerm"
            data-testid="fullscreen-btn"
          >
            <Maximize2 className="h-4 w-4" />
          </button>

          <SpinWheel
            segments={wheel.segments}
            spinSequence={spinSequence}
            onSegmentReached={handleSegmentReached}
            onSpinComplete={() => setAnimDone(true)}
            size={Math.min(380, typeof window !== "undefined" ? window.innerWidth - 80 : 380)}
          />

          {/* Respin-flash overlay – vises kort når hjulet stopper på et respin-segment */}
          {respinFlash && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none z-10" data-testid="respin-flash">
              <div className="bg-[#5BA5C9]/90 text-black px-6 py-3 font-fraunces italic text-2xl rounded-sm shadow-lg animate-pulse">
                🎉 Bonusrunde! <span className="font-meta text-xs not-italic ml-2">«{respinFlash.label}»</span>
              </div>
            </div>
          )}

          {/* Resultat */}
          {result && animDone && (
            <div
              className="mt-8 border border-[var(--magenta)]/30 bg-[var(--magenta)]/10 p-6 text-center max-w-md"
              data-testid="spin-result"
            >
              <Gift className="h-8 w-8 text-[var(--magenta)] mx-auto mb-3" />
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">
                {t("wheel.you_won")}
              </div>
              <div className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">
                {result.spin.segment_label}
              </div>
              <div className="text-xs text-[var(--cream-soft)] font-manrope leading-relaxed">
                {result.is_anonymous ? (
                  <>
                    {creator.username} {t("wheel.notified_anon", "har fått beskjed og tar kontakt med deg på")} <strong className="text-[var(--cream)]">{extPlatform}</strong>.<br />
                  </>
                ) : (
                  <>{creator.username} {t("wheel.notified_member", "har fått beskjed og tar kontakt med deg på den eksterne siden hvor du kjøpte koden. En bekreftelse er sendt til din innboks")}.<br /></>
                )}
                {result.claim_id && (
                  <span className="text-[var(--magenta)]">Claim-ID: <strong className="font-mono">{result.claim_id}</strong></span>
                )} — {t("wheel.show_claim_id", "vis denne til creatoren for å bekrefte at det er deg")}.
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-4 justify-center">
                <Link
                  to={`/lykkehjul/vinst/${result.spin.id}`}
                  className="inline-block px-4 py-1.5 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-white hover:opacity-90"
                  data-testid="view-vinst-page"
                >
                  {t("wheel.see_details", "SE DETALJER")}
                </Link>
                <Link
                  to={`/meldinger?til=Admin`}
                  className="inline-block px-4 py-1.5 text-xs font-meta tracking-[0.18em] border border-white/20 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
                  data-testid="contact-admin-fallback"
                >
                  {t("wheel.problem_contact_admin", "PROBLEM? KONTAKT ADMIN")}
                </Link>
              </div>
            </div>
          )}

          {/* Kode-input */}
          {!result && (
            <div className="mt-8 w-full max-w-md" data-testid="code-input-area">
              {/* Anonym-felter (kun når ikke innlogget) */}
              {!user && (
                <div className="mb-6 border border-[var(--magenta)]/30 bg-[var(--magenta)]/5 p-4">
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">
                    {t("wheel.where_contact_you", "HVOR KAN CREATOREN KONTAKTE DEG?")}
                  </div>
                  <p className="text-xs text-[var(--cream-soft)] mb-3 leading-relaxed">
                    {t("wheel.where_contact_intro", "Du har ikke konto her. Oppgi plattformen og brukernavnet ditt der du fikk koden, så creator vet hvor de skal sende premien.")}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={extPlatform}
                      onChange={(e) => setExtPlatform(e.target.value)}
                      data-testid="anon-platform"
                      className="bg-[var(--noir)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
                    >
                      <option value="Snapchat">Snapchat</option>
                      <option value="OnlyFans">OnlyFans</option>
                      <option value="Instagram">Instagram</option>
                      <option value="TikTok">TikTok</option>
                      <option value="X (Twitter)">X (Twitter)</option>
                      <option value="Annet">{t("common.other", "Annet")}</option>
                    </select>
                    <input
                      value={extHandle}
                      onChange={(e) => setExtHandle(e.target.value)}
                      placeholder={t("wheel.your_handle_there")}
                      maxLength={60}
                      data-testid="anon-handle"
                      className="bg-[var(--noir)] border border-white/15 px-3 py-2 text-sm text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
                    />
                  </div>
                </div>
              )}

              <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2">
                {t("wheel.paste_code_you_got", "LIM INN KODEN DU FIKK")}
              </label>
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t("wheel.code_placeholder")}
                  maxLength={12}
                  disabled={busy || spinSequence !== null}
                  className="flex-1 bg-[var(--noir)] border border-white/15 px-4 py-3 text-center text-lg font-manrope tracking-[0.3em] text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
                  data-testid="code-input"
                />
                <button
                  onClick={spin}
                  disabled={busy || spinSequence !== null || !code.trim()}
                  className="px-6 py-3 font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50"
                  data-testid="spin-btn"
                >
                  {spinSequence !== null ? t("wheel.spinning") : t("wheel.spin_btn")}
                </button>
              </div>
              <p className="font-meta text-[10px] text-[var(--cream-faint)] mt-3 text-center">
                {t("wheel.codes_are_single_use", "Koder er engangsbruk og fås av creatoren ved kjøp på deres eksterne plattformer.")}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* FULLSKJERM-MODAL */}
      {fullscreen && (
        <FullscreenWheel
          wheel={wheel}
          creator={creator}
          spinSequence={spinSequence}
          onSegmentReached={handleSegmentReached}
          onSpinComplete={() => setAnimDone(true)}
          result={result}
          animDone={animDone}
          respinFlash={respinFlash}
          onClose={() => setFullscreen(false)}
          code={code}
          setCode={setCode}
          spin={spin}
          busy={busy}
        />
      )}
    </Layout>
  );
}

function FullscreenWheel({ wheel, creator, spinSequence, onSegmentReached, onSpinComplete, result, animDone, respinFlash, onClose, code, setCode, spin, busy }) {
  // ESC key + klikk utenfor lukker
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Skjul scroll på body
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const wheelSize = Math.min(
    typeof window !== "undefined" ? Math.min(window.innerWidth, window.innerHeight) - 280 : 500,
    640
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black flex flex-col items-center justify-center p-4 overflow-y-auto"
      data-testid="fullscreen-modal"
      onClick={onClose}
    >
      {/* Lukk-knapp – stor, tydelig */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-5 right-5 flex items-center gap-2 px-4 py-2 bg-[var(--magenta)] text-black font-meta text-xs tracking-[0.2em] hover:opacity-90 z-10"
        data-testid="fullscreen-close"
      >
        <X className="h-4 w-4" /> LUKK FULLSKJERM
      </button>

      {/* Hint nederst */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] text-center">
        TRYKK ESC ELLER KLIKK UTENFOR HJULET FOR Å LUKKE
      </div>

      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center w-full max-w-2xl">
        <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-1 text-center">{wheel.title}</h2>
        <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-4">
          {creator.username?.toUpperCase()}
        </div>
        <div className="relative">
          <SpinWheel
            segments={wheel.segments}
            spinSequence={spinSequence}
            onSegmentReached={onSegmentReached}
            onSpinComplete={onSpinComplete}
            size={wheelSize}
          />
          {respinFlash && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none z-10">
              <div className="bg-[#5BA5C9]/90 text-black px-6 py-3 font-fraunces italic text-3xl rounded-sm shadow-lg animate-pulse">
                🎉 Bonusrunde! <span className="font-meta text-sm not-italic ml-2">«{respinFlash.label}»</span>
              </div>
            </div>
          )}
        </div>

        {/* Resultat eller kode-input */}
        {result && animDone ? (
          <div className="mt-6 text-center" data-testid="fullscreen-result">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-1">DU VANT</div>
            <div className="font-fraunces italic text-3xl text-[var(--magenta)] mb-3">{result.spin.segment_label}</div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link
                to={`/lykkehjul/vinst/${result.spin.id}`}
                className="inline-block px-4 py-2 text-xs font-meta tracking-[0.18em] bg-[var(--magenta)] text-white hover:opacity-90"
              >
                SE DETALJER
              </Link>
              <Link
                to={`/meldinger?til=Admin`}
                className="inline-block px-4 py-2 text-xs font-meta tracking-[0.18em] border border-white/30 text-[var(--cream-soft)] hover:border-[var(--magenta)] hover:text-[var(--magenta)]"
              >
                PROBLEM? KONTAKT ADMIN
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6 w-full max-w-md" data-testid="fullscreen-code-area">
            <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-soft)] mb-2 text-center">
              LIM INN KODEN DU FIKK
            </label>
            <div className="flex gap-2">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="F.EKS 4HYRBKG4"
                maxLength={12}
                disabled={busy || spinSequence !== null}
                className="flex-1 bg-[var(--noir-elev)] border border-white/15 px-4 py-3 text-center text-lg font-manrope tracking-[0.3em] text-[var(--cream)] outline-none focus:border-[var(--magenta)]"
                data-testid="fullscreen-code-input"
              />
              <button
                onClick={spin}
                disabled={busy || spinSequence !== null || !code.trim()}
                className="px-6 py-3 font-meta tracking-[0.18em] bg-[var(--magenta)] text-black disabled:opacity-50"
                data-testid="fullscreen-spin-btn"
              >
                {spinSequence !== null ? "SPINNER…" : "SPINN"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
